import { useCallback, useEffect, useRef, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

/**
 * Brouillon local d'un formulaire de création (localStorage).
 *
 * Un commercial interrompu (appel entrant, onglet fermé) retrouve ce qu'il avait saisi. Règles :
 * - la clé porte l'utilisateur, le formulaire et son contexte (fiche CRM, inscription reprise) :
 *   deux prospects ou deux utilisateurs ne partagent jamais un brouillon ;
 * - seuls les champs que l'utilisateur a modifiés (`dirtyFields`) sont gardés : un champ
 *   prérempli ou calculé par le serveur n'est pas figé dans le brouillon ;
 * - la restauration attend `ready` (préremplissage terminé) et passe par `setValue`, les
 *   `defaultValues` d'un formulaire étant figées au montage : ce que l'utilisateur avait tapé
 *   l'emporte sur le préremplissage, le reste garde la valeur préremplie ;
 * - sauvegarde différée (1 s), expiration à 7 jours, taille plafonnée, fichiers exclus ;
 * - tout accès au stockage est protégé (navigation privée, quota) : sans stockage, le
 *   formulaire fonctionne simplement sans brouillon.
 */

export const FORM_DRAFT_PREFIX = "walter-crm:form-draft:v1:";
export const FORM_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const FORM_DRAFT_MAX_BYTES = 100_000;
export const FORM_DRAFT_DEBOUNCE_MS = 1000;

type StoredDraft = { savedAt: string; values: Record<string, unknown> };

function storage(): Storage | null {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch {
        return null;
    }
}

/**
 * Clé de stockage d'un brouillon : utilisateur + formulaire + contexte (paramètres triés, vides
 * ignorés). `null` quand l'utilisateur est inconnu : sans lui, pas de brouillon.
 */
export function buildFormDraftKey(userId: number | string | null | undefined, formKey: string, context?: Record<string, unknown>): string | null {
    if (userId == null || userId === "") return null;
    const contextPart = Object.entries(context ?? {})
        .filter(([, value]) => value != null && value !== "")
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}`)
        .join("&");
    return `${FORM_DRAFT_PREFIX}${userId}:${formKey}${contextPart ? `?${contextPart}` : ""}`;
}

function parse(raw: string | null): StoredDraft | null {
    if (!raw) return null;
    try {
        const draft = JSON.parse(raw) as StoredDraft;
        if (!draft || typeof draft.savedAt !== "string" || typeof draft.values !== "object" || draft.values === null) return null;
        return draft;
    } catch {
        return null;
    }
}

function isExpired(draft: StoredDraft, now: number): boolean {
    const savedAt = Date.parse(draft.savedAt);
    return Number.isNaN(savedAt) || now - savedAt > FORM_DRAFT_TTL_MS;
}

/** Lit un brouillon encore valide (un brouillon expiré ou illisible est supprimé). */
export function readFormDraft(key: string, now = Date.now()): StoredDraft | null {
    const store = storage();
    if (!store) return null;
    try {
        const draft = parse(store.getItem(key));
        if (draft && !isExpired(draft, now)) return draft;
        store.removeItem(key);
    } catch {
        // Stockage indisponible : pas de brouillon.
    }
    return null;
}

/** Écrit un brouillon ; `false` s'il est trop gros ou si le stockage refuse (quota, mode privé). */
export function writeFormDraft(key: string, values: Record<string, unknown>, now = Date.now()): boolean {
    const store = storage();
    if (!store) return false;
    try {
        const raw = JSON.stringify({ savedAt: new Date(now).toISOString(), values } satisfies StoredDraft);
        if (raw.length > FORM_DRAFT_MAX_BYTES) {
            store.removeItem(key);
            return false;
        }
        store.setItem(key, raw);
        return true;
    } catch {
        try {
            store.removeItem(key);
        } catch {
            // ignore
        }
        return false;
    }
}

export function removeFormDraft(key: string): void {
    try {
        storage()?.removeItem(key);
    } catch {
        // ignore
    }
}

/** Supprime les brouillons expirés de l'utilisateur dont la clé commence par `userPrefix`. */
export function purgeExpiredFormDrafts(userPrefix: string, now = Date.now()): void {
    const store = storage();
    if (!store) return;
    try {
        const keys: string[] = [];
        for (let index = 0; index < store.length; index++) {
            const key = store.key(index);
            if (key && key.startsWith(userPrefix)) keys.push(key);
        }
        keys.forEach(key => {
            const draft = parse(store.getItem(key));
            if (!draft || isExpired(draft, now)) store.removeItem(key);
        });
    } catch {
        // ignore
    }
}

/** Valeurs à garder : les champs modifiés par l'utilisateur, hors champs exclus. */
export function draftValues(values: Record<string, unknown>, dirtyFields: Record<string, unknown>, exclude: ReadonlySet<string>): Record<string, unknown> {
    const kept: Record<string, unknown> = {};
    Object.keys(dirtyFields).forEach(name => {
        if (exclude.has(name) || !isDirtyEntry(dirtyFields[name])) return;
        const value = values[name];
        if (value === undefined || value instanceof Blob) return;
        kept[name] = value;
    });
    return kept;
}

function isDirtyEntry(entry: unknown): boolean {
    if (entry === true) return true;
    if (Array.isArray(entry)) return entry.some(isDirtyEntry);
    if (entry && typeof entry === "object") return Object.values(entry).some(isDirtyEntry);
    return false;
}

type UseFormDraftOptions<T extends FieldValues> = {
    /** Clé de `buildFormDraftKey` ; `null` désactive le brouillon. */
    storageKey: string | null;
    form: UseFormReturn<T>;
    enabled: boolean;
    /** Vrai quand le préremplissage asynchrone est terminé (ou absent) : la restauration attend. */
    ready: boolean;
    /** Champs jamais stockés (fichiers, valeurs dérivées). */
    exclude?: readonly string[];
    /**
     * Appelé avec les valeurs du brouillon juste avant leur restauration : le formulaire peut
     * neutraliser les effets que ces valeurs déclencheraient (un choix qui réécrit d'autres champs).
     */
    onRestore?: (values: Record<string, unknown>) => void;
};

export type FormDraftState = {
    /** Date de sauvegarde du brouillon restauré, `null` sans restauration. */
    restoredAt: Date | null;
    /** Efface le brouillon et remet les champs restaurés à leur valeur d'avant restauration. */
    discard: () => void;
    /** Efface le brouillon (à appeler après un envoi réussi). */
    clear: () => void;
};

export function useFormDraft<T extends FieldValues>({ storageKey, form, enabled, ready, exclude = [], onRestore }: UseFormDraftOptions<T>): FormDraftState {
    const onRestoreRef = useRef(onRestore);
    onRestoreRef.current = onRestore;
    const active = enabled && Boolean(storageKey);
    const [restoredAt, setRestoredAt] = useState<Date | null>(null);
    // Valeurs d'avant restauration, pour « Effacer le brouillon ».
    const beforeRestore = useRef<Record<string, unknown>>({});
    // Champs remis à leur valeur par « Effacer » : pas réenregistrés tant qu'on n'y touche pas.
    const discarded = useRef<Record<string, string>>({});
    // Rien n'est écrit avant la tentative de restauration : sinon un formulaire vierge
    // écraserait le brouillon qu'on s'apprête à relire.
    const restoreDone = useRef(false);
    const excludeKey = exclude.join("\0");
    const excluded = useRef<ReadonlySet<string>>(new Set(exclude));
    excluded.current = new Set(exclude);

    // Une nouvelle clé (autre prospect, autre formulaire) repart de zéro.
    useEffect(() => {
        restoreDone.current = false;
        beforeRestore.current = {};
        discarded.current = {};
        setRestoredAt(null);
    }, [storageKey, enabled]);

    // Purge des brouillons expirés de cet utilisateur.
    useEffect(() => {
        if (!active || !storageKey) return;
        const userPrefix = storageKey.slice(0, storageKey.indexOf(":", FORM_DRAFT_PREFIX.length) + 1);
        if (userPrefix) purgeExpiredFormDrafts(userPrefix);
    }, [active, storageKey]);

    // Restauration, une fois le préremplissage terminé.
    useEffect(() => {
        if (!active || !storageKey || !ready || restoreDone.current) return;
        restoreDone.current = true;
        const draft = readFormDraft(storageKey);
        if (!draft) return;
        const entries = Object.entries(draft.values).filter(([name]) => !excluded.current.has(name));
        if (entries.length === 0) return;
        onRestoreRef.current?.(Object.fromEntries(entries));
        const previous: Record<string, unknown> = {};
        entries.forEach(([name, value]) => {
            previous[name] = form.getValues(name as never);
            form.setValue(name as never, value as never, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
        });
        beforeRestore.current = previous;
        setRestoredAt(new Date(draft.savedAt));
    }, [active, storageKey, ready, form]);

    // Lu au rendu pour que React Hook Form calcule `dirtyFields` (état abonné par proxy).
    void form.formState.dirtyFields;

    // Sauvegarde différée à chaque modification ; une saisie encore en attente est écrite quand
    // le formulaire se ferme ou change de clé.
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (!active || !storageKey) return;
        const save = () => {
            timer.current = null;
            if (!restoreDone.current) return;
            const values = draftValues(form.getValues() as Record<string, unknown>, form.formState.dirtyFields as Record<string, unknown>, excluded.current);
            Object.entries(discarded.current).forEach(([name, serialized]) => {
                if (JSON.stringify(values[name]) === serialized) delete values[name];
                else delete discarded.current[name];
            });
            if (Object.keys(values).length === 0) removeFormDraft(storageKey);
            else writeFormDraft(storageKey, values);
        };
        const subscription = form.watch(() => {
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(save, FORM_DRAFT_DEBOUNCE_MS);
        });
        return () => {
            subscription.unsubscribe();
            if (timer.current) {
                clearTimeout(timer.current);
                save();
            }
        };
        // `excludeKey` stands for `exclude`, read through a ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, storageKey, form, excludeKey]);

    const clear = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
        if (storageKey) removeFormDraft(storageKey);
        beforeRestore.current = {};
        setRestoredAt(null);
    }, [storageKey]);

    const discard = useCallback(() => {
        // `setValue`, not `resetField`: most generic inputs are controlled through `setValue` and
        // never registered, which `resetField` ignores. Dirtiness is recomputed against the defaults.
        Object.entries(beforeRestore.current).forEach(([name, value]) => {
            discarded.current[name] = JSON.stringify(value);
            form.setValue(name as never, value as never, { shouldDirty: true, shouldValidate: true });
        });
        clear();
    }, [clear, form]);

    return { restoredAt, discard, clear };
}
